import { useState } from 'react'
import { Bot as BotIcon, ChevronRight, Loader2 } from 'lucide-react'
import { Bot } from '@/shared/types'

interface BotCardProps {
  bot: Bot
  onClick?: () => void
}

export default function BotCard({ bot, onClick }: BotCardProps) {
  const isActive = bot.status === 'active' && bot.enabled
  const [imageLoading, setImageLoading] = useState(true)
  const [imageError, setImageError] = useState(false)

  const getLogoUrl = () => {
    if (!bot.id) return null

    const baseUrl = import.meta.env.VITE_OPSERVICE_BASE_URL || 'http://localhost:8080'
    return `${baseUrl}/api/v1/bots/${bot.id}/logo`
  }

  const logoUrl = getLogoUrl()

  return (
    <div
      onClick={onClick}
      className="group relative bg-tg-secondary-bg rounded-2xl p-4 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 cursor-pointer overflow-hidden"
      style={{
        boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.15)'
      }}
    >
      {/* Gradient overlay for active bots */}
      {isActive && (
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/5 to-transparent rounded-full blur-2xl" />
      )}

      <div className="relative flex items-center gap-3">
        {/* Bot Logo or Icon */}
        <div className={`relative flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center ${
          isActive
            ? 'bg-tg-button shadow-lg shadow-tg-button/25'
            : 'bg-tg-bg'
        }`}>
          {logoUrl && !imageError ? (
            <>
              <img
                src={logoUrl}
                alt={`${bot.name} logo`}
                className={`w-full h-full object-cover transition-opacity duration-200 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                onError={() => {
                  setImageError(true)
                  setImageLoading(false)
                }}
                onLoad={() => {
                  setImageLoading(false)
                }}
              />
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className={`w-5 h-5 animate-spin ${isActive ? 'text-tg-button-text' : 'text-[#6d7885]'}`} />
                </div>
              )}
            </>
          ) : (
            <BotIcon className={`w-6 h-6 ${isActive ? 'text-tg-button-text' : 'text-[#6d7885]'}`} />
          )}
        </div>

        {/* Bot Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-tg-text truncate mb-0.5">
            @{bot.username}
          </h3>
          <p className="text-sm text-tg-hint truncate">{bot.name}</p>
        </div>

        {/* Arrow */}
        <ChevronRight className="w-5 h-5 text-tg-hint/50 group-hover:text-tg-hint group-hover:translate-x-1 transition-all flex-shrink-0" />
      </div>

      {/* Role badge */}
      {bot.user_role && bot.user_role !== 'owner' && (
        <div className="mt-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${
            bot.user_role === 'admin'
              ? 'bg-purple-500/10 text-purple-600 border border-purple-500/30'
              : 'bg-[#5288c1]/10 text-[#5288c1] border border-[#5288c1]/30'
          }`}>
            {bot.user_role === 'admin' ? 'Админ' : 'Редактор'}
          </span>
        </div>
      )}

      {/* Status badge */}
      <div className="mt-4 space-y-2">
        {(() => {
          // Determine bot status based on status and enabled fields
          let status: 'active' | 'inactive' | 'suspended' | 'pending_verification'
          if (bot.status === 'active' && bot.enabled) {
            status = 'active'
          } else if (bot.status === 'suspended') {
            status = 'suspended'
          } else if (bot.status === 'pending_verification') {
            status = 'pending_verification'
          } else {
            status = 'inactive'
          }

          const statusStyles = {
            active: {
              label: 'Активен',
              bgColor: 'bg-green-500/10',
              textColor: 'text-green-600',
              borderColor: 'border-green-500/30',
              dotColor: 'bg-green-500',
            },
            inactive: {
              label: 'Неактивен',
              bgColor: 'bg-gray-500/10',
              textColor: 'text-gray-600',
              borderColor: 'border-gray-500/30',
              dotColor: 'bg-gray-500',
            },
            pending_verification: {
              label: 'Ожидает проверки',
              bgColor: 'bg-yellow-500/10',
              textColor: 'text-yellow-600',
              borderColor: 'border-yellow-500/30',
              dotColor: 'bg-yellow-500',
            },
            suspended: {
              label: 'Приостановлен',
              bgColor: 'bg-red-500/10',
              textColor: 'text-red-500',
              borderColor: 'border-red-500/30',
              dotColor: 'bg-red-500',
            },
          }

          const style = statusStyles[status]

          return (
            <div>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${style.bgColor} ${style.textColor} border ${style.borderColor}`}>
                <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${style.dotColor}`} />
                {style.label}
              </span>
            </div>
          )
        })()}
      </div>
    </div>
  )
}
